/**
 * Centralised media URL resolver — the single source of truth for image sources.
 *
 * Stored media (product/category/banner/service images, store photos, order item
 * thumbnails, review photos) lives in MongoDB in one of three shapes:
 *
 *   1. Backend private-proxy path — what uploads write today, because the
 *      Cloudflare R2 bucket is private and R2_PUBLIC_BASE_URL is unset:
 *        /api/media/r2/products/<id>/<uuid>.jpg
 *   2. A bare storage object key:
 *        products/<id>/<uuid>.jpg      (or  r2/products/<id>/<uuid>.jpg)
 *   3. An absolute external URL — the seeded catalogue and the admin
 *      "Add URL" field:
 *        https://images.unsplash.com/...
 *
 * The storefront is served from a different origin than the API, so
 * `<img src="/api/media/...">` resolves against the *frontend* host and 404s
 * (broken image). Every <img> that renders stored media must go through
 * resolveMediaUrl(), which:
 *   - leaves absolute (https://, http://, //) and inline (data:, blob:) URLs as-is,
 *   - points site-relative paths at the configured backend origin,
 *   - turns bare R2 object keys into the existing private proxy endpoint,
 *   - returns a neutral inline placeholder for empty/invalid values.
 *
 * R2 stays private: the browser only ever calls the backend proxy
 * GET /api/media/{path}; credentials/object URLs never reach React.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/** Backend origin used for media/api requests (no trailing slash). */
export const BACKEND_ORIGIN = BACKEND_URL.replace(/\/+$/, "");

const PLACEHOLDER_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400" role="img" aria-label="Image unavailable">' +
  '<rect width="400" height="400" fill="#f1f5f9"/>' +
  '<g fill="none" stroke="#cbd5e1" stroke-width="10" stroke-linecap="round" stroke-linejoin="round">' +
  '<rect x="110" y="128" width="180" height="144" rx="14"/><circle cx="162" cy="178" r="15"/>' +
  '<path d="M110 248l58-52 44 38 34-28 44 42"/></g></svg>';

/**
 * Neutral inline placeholder, used only when there is no usable image value or
 * when a stored image genuinely fails to load — never to replace a valid URL.
 */
export const FALLBACK_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(PLACEHOLDER_SVG)}`;

// Bare object keys written by backend/storage.py (`products/...`, `reviews/...`).
const OBJECT_KEY_RE = /^(r2\/)?(products|reviews)\/[A-Za-z0-9_-]+\/[A-Za-z0-9._-]+$/;

/**
 * Resolve a stored media value into a browser-loadable image URL.
 *
 * @param {string} value stored image value (URL, /api/media/... path, or R2 key)
 * @returns {string} absolute https URL, inline data/blob URL, or the fallback placeholder
 */
export function resolveMediaUrl(value) {
  if (typeof value !== "string") return FALLBACK_IMAGE;
  const url = value.trim();
  if (!url || url === "undefined" || url === "null") return FALLBACK_IMAGE;
  // Inline/object URLs need no rewriting.
  if (/^(data|blob):/i.test(url)) return url;
  // Already absolute (external https images must be preserved), incl. //host/path.
  if (/^https?:\/\//i.test(url) || url.startsWith("//")) return url;
  // Backend-relative path (e.g. /api/media/r2/products/<id>/<uuid>.jpg): the
  // storefront lives on another origin, so prefix the API origin.
  if (url.startsWith("/")) return BACKEND_ORIGIN ? `${BACKEND_ORIGIN}${url}` : url;
  // Bare R2 object key -> existing private media proxy.
  if (OBJECT_KEY_RE.test(url)) {
    const path = url.startsWith("r2/") ? `/api/media/${url}` : `/api/media/r2/${url}`;
    return BACKEND_ORIGIN ? `${BACKEND_ORIGIN}${path}` : path;
  }
  // Unknown shape: hand it to the browser unchanged (it may be a frontend asset
  // path); onError -> placeholder handles a genuinely dead URL.
  return url;
}

/**
 * `<img onError>` handler: swap in the placeholder once so a dead URL cannot
 * loop forever if the placeholder itself fails.
 */
export function handleMediaError(event) {
  const img = event?.currentTarget;
  if (!img || img.dataset.mediaFallback === "1") return;
  img.dataset.mediaFallback = "1";
  img.src = FALLBACK_IMAGE;
}
